package com.neurobs.ccabserver.transcriptreview.states

import com.neurobs.ccab.CCABServer
import com.neurobs.ccab.casr.{ReviewedTestTranscriptCreateData, ReviewedTestTranscriptUpdateData}
import com.neurobs.ccabserver.transcriptreview.util.{TestTranscriptReviewHandler}
import com.neurobs.ccab.db.{ReviewedTestTranscriptData}
import com.neurobs.ccabserver.transcriptreview.components.transcriptreview.components.TranscriptWordIndex
import com.neurobs.errors.Result
import com.neurobs.react.{BooleanAttribute, GenericState, GenericStateGenerator, RegisteredChangeAttribute, StateAttribute}
import com.neurobs.util.Interval
import com.neurobs.util.TimeSeconds
import com.neurobs.util.TimeSeconds.TimeSecondsInterval
import org.scalajs.dom
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.React.createRef
import slinky.core.facade.{ReactElement, ReactRef}
import scala.concurrent.ExecutionContext.Implicits.global

import scala.concurrent.Future

case class TestTranscriptReviewState
(
  handler: StateAttribute[TestTranscriptReviewHandler],
  audioViewers: StateAttribute[AudioViewerSequenceState],
  latency: BooleanAttribute,
  soundPlaying: BooleanAttribute,
  pixelsPerSecond: StateAttribute[Int],
  zoomScale: StateAttribute[Double],
  registeredChange: RegisteredChangeAttribute,
  containerRef: ReactRef[dom.html.Div],
  commandRouter: CommandRouter
) extends GenericState {
  def registerChange() = registeredChange.register()

  def saveReviewedTranscript(userId: Long, username: String)(implicit server: CCABServer): Future[Result[ReviewedTestTranscriptData]] = {
	  val testRunId = handler.value.sourceData.serverData.selection.transcript.testRunId
	  val reviewedTestTranscriptJson = handler.value.toReviewedTestTranscriptJson(username, latency.value)
	  handler.value.reviewedTestTranscriptId match {
		  case None =>
				println("No reviewedTestTranscriptId")
			  val data = ReviewedTestTranscriptCreateData(testRunId = testRunId, userId = userId, reviewedTestTranscript = reviewedTestTranscriptJson, forLatency = latency.value)
			  server.createReviewedTestTranscript(data)._2.map(_.map{d =>
					println(s"Setting reviewedTestTranscriptId to ${d.id}")
					handler.value.reviewedTestTranscriptId = Some(d.id); d})
		  case Some(reviewedTestTranscriptId) =>
				println(s"reviewedTestTranscriptId = $reviewedTestTranscriptId")
			  val data = ReviewedTestTranscriptUpdateData(id = reviewedTestTranscriptId, reviewedTestTranscript = reviewedTestTranscriptJson, forLatency = latency.value)
			  server.updateReviewedTestTranscript(data)._2
	  }
  }

  def setScroll(pixels: Double) = {
    if (containerRef.current != null) {
      containerRef.current.scrollLeft = pixels.max(0)
    }
  }

	def setScrollToWord(recordingIndex: Int, transcriptInterval: TimeSecondsInterval): Unit = {
		val container = containerRef.current
		if (container != null) {
			val recordings = handler.value.sourceData.recordings
			recordings.lift(recordingIndex) foreach { recording =>
				val midTranscriptTime = (transcriptInterval.begin + transcriptInterval.end) / 2
				//println(s"midTranscriptTime = $midTranscriptTime")
				val midTime = handler.value.sourceData.combinedSegmentsTime(recordingIndex, midTranscriptTime)
				//println(s"midTime = $midTime")
				val midDistance = secondsToPixels(midTime)
				//println(s"midDistance = $midDistance")
				//println(s"container.clientWidth = ${container.clientWidth}")
				val scrollLeft = midDistance - container.clientWidth / 2
				//println(s"scrollLeft = $scrollLeft")
				setScroll(scrollLeft)
			}
		}
	}

  def playPause(): Unit = audioViewers.value.playPause()
  def audioIsPlaying: Boolean = soundPlaying.value

	def play(index: Int, transcriptInterval: TimeSecondsInterval): Unit = {
		val offset = handler.value.recordings(index).sourceData.audioSegment.interval.begin
		audioViewers.value.play(index = index, interval = Interval[TimeSeconds](transcriptInterval.begin - offset, transcriptInterval.end - offset))
	}

  def PPS: Int = pixelsPerSecond.value

  def secondsToPixels(seconds: TimeSeconds): Double = {
    seconds.seconds() * PPS
  }

  def pixelsToSeconds(pixels: Double): TimeSeconds = {
    TimeSeconds(pixels / PPS)
  }

  def getZoomScale = zoomScale.value

  def getTransformStyle = s"scale(${getZoomScale}, 1)"

  def zoomIn() = {
    setState(zoomScale, getZoomScale + .1)
    registerChange()
  }

  def zoomOut() = {
    if (getZoomScale > .5) setState(zoomScale, getZoomScale - .1)
    registerChange()
  }

  def setZoomScale(zoom: Double) = setState(zoomScale, zoom)

  def hasWordOverlap(): Boolean = {
    handler.value.hasWordOverlap()
  }

  def resolveWordOverlap() = {
    handler.value.resolveWordOverlap()
    registerChange()
  }

	def selectWord(recordingIndex: Int, transcriptInterval: TimeSecondsInterval): Unit = {
		setScrollToWord(recordingIndex = recordingIndex, transcriptInterval = transcriptInterval)
		play(index = recordingIndex, transcriptInterval = transcriptInterval)
	}

	def onPlaying(recordingIndex: Int, time: TimeSeconds): Unit = {
		handler.value.recordings.lift(recordingIndex) foreach { recording =>
			val transcriptTime = handler.value.sourceData.recordings(recordingIndex).audioSegment.interval.begin + time
			val trans = recording.reviewingTranscript
			val i = trans.transcriptWords.indexWhere(_.startTime > transcriptTime)
			val highlighted: Option[TranscriptWordIndex] = {
				if (i == 0) None
				else if (i > 0) Some(TranscriptWordIndex(recordingIndex = recordingIndex, wordIndex = i - 1))
				else if (trans.transcriptWords.nonEmpty) Some(TranscriptWordIndex(recordingIndex = recordingIndex, wordIndex = trans.transcriptWords.length - 1))
				else None
			}
			commandRouter.setTranscriptTextHighlightedWord(highlighted)
		}
	}

//  def setInitialScroll() = {
//    reviewHandler.earliestWordStart match {
//      case None =>
//      case Some(startTime) =>
//        setScroll(secondsToPixels(startTime) - 20)
//    }
//  }


}

object TestTranscriptReviewState {
  val DEFAULT_PPS = 375
}

object TestTranscriptReviewStateGenerator extends GenericStateGenerator[TestTranscriptReviewState] {
  override def generateEmptyDefaultState(): TestTranscriptReviewState =
    TestTranscriptReviewState(null, null, null, null, null, null, null, null, null)
}

@react
object TestTranscriptReviewStateProvider {

  case class Props(sourceData: TestTranscriptReviewData, children: ReactElement*)

  val component = FunctionalComponent[Props] { props =>

	  val commandRouter = CommandRouterContext.accessState()
		val userState = UserState.accessState()

    val soundPlaying = BooleanAttribute(useState(default = false))
    val handler = StateAttribute(useState[TestTranscriptReviewHandler](() => TestTranscriptReviewHandler(props.sourceData, userState.userId)))
    val audioViewers = StateAttribute(useState[AudioViewerSequenceState](() => {
      new AudioViewerSequenceState(buffers = props.sourceData.recordings.map(_.audioSegment.data))
    }))
    val latency = BooleanAttribute(useState(default = false))
    val registeredChange = RegisteredChangeAttribute(useState(default = 0))
    val pixelsPerSecond = StateAttribute(useState[Int](TestTranscriptReviewState.DEFAULT_PPS))
    val zoomScale = StateAttribute(useState[Double](default = 1))
    val containerRef = createRef[dom.html.Div]

    val testTranscriptReviewState = TestTranscriptReviewState(
      handler = handler,
      audioViewers = audioViewers,
      latency = latency,
      soundPlaying = soundPlaying,
      pixelsPerSecond = pixelsPerSecond,
      zoomScale = zoomScale,
      registeredChange = registeredChange,
      containerRef = containerRef,
		 commandRouter = commandRouter
    )

	  audioViewers.value.delegate = new AudioViewerSequenceState.Delegate {
		  override def setPlaying(playing: Boolean): Unit = soundPlaying.setValue(playing)
		  override def onPlaying(recordingIndex: Int, time: TimeSeconds): Unit = testTranscriptReviewState.onPlaying(recordingIndex, time)
	  }

    TestTranscriptReviewStateGenerator.Provider(testTranscriptReviewState)(
      props.children:_*
    )
  }
}
